import React, { useState, useEffect, useCallback } from 'react';
import { Tooltip } from '@mui/material';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import appdata from '../../../Services/appdata';
import { creators as ProjectedFinancialPerformanceActions } from '../../../Reducers/Reporting/ProjectedFinancialPerformance';
import { creators as ReportSettingActions } from '../../../Reducers/Reporting/ReportSetting';
import YearSelector from '../../../Common/Selectors/YearSelectorSingle';
import { financialMonth, makeDimDate } from '../../../Utils/Functions';
import TopChart from './TopChart';
import BottomChart from './BottomChart';

const ProjectedFinancialPerformance = ({
  dir,
  tableStructure,
  selectedCompany,
  defaultYear,
  defaultMonth,
  defaultStartMonth,
  defaultDimDate,
  projectedFinancialPerformanceSetting,
  profitSetting,
  tabIndex,
  bubbleFlag,
}) => {
  const dispatch = useDispatch();
  const projectedFinancialPerformanceSummary = useSelector((state) => state.Reporting.Summary.reportingSummaries?.projectedFinancialPerformance ?? '');
  const summaryData = useSelector(state => state.Reporting.projectedfinancialPerformance.summaryData);
  const detailData = useSelector(state => state.Reporting.projectedfinancialPerformance.detailData);
  const lastFetchedCompany = useSelector(
    (state) => state?.Reporting?.Default?.defaultCompany ?? null,
  );
  const lastFetchedDimDate = useSelector(
    (state) => state?.Reporting?.Default?.defaultDimDate ?? null,
  );

  const [resize, setResize] = useState(false);
  const [label, setLabel] = useState('');
  const [selectedYears, setSelectedYears] = useState([defaultYear]);
  const [selectedTopItems, setSelectedTopItems] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [selectedOption, setSelectedOption] = useState('Forecast');
  const [showPercent, setShowPercent] = useState(false);
  const [profitName, setProfitName] = useState('Profit / Loss');
  const [pprofitName, setPprofitName] = useState('% Profit');

  const handleResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);


  useEffect(() => {
    const initializeComponent = async () => {
      if (projectedFinancialPerformanceSetting) {
        const settings = JSON.parse(projectedFinancialPerformanceSetting);
        setSelectedOption(settings.selectedOption);
        setShowPercent(settings.showPercent === true);
      } else {
        await dispatch(ReportSettingActions.reportSettingUpdateRequest({
          ProjectedFinancialPerformance: JSON.stringify({
            selectedOption: 'Forecast',
            showPercent: false,
          }),
        }));
      }

      if (profitSetting) {
        const settings = JSON.parse(profitSetting);
        setProfitName(settings.profitName);
        setPprofitName(settings.pprofitName);
      } else {
        await dispatch(ReportSettingActions.reportSettingUpdateRequest({
          ProfitTitle: JSON.stringify({
            profitName: 'Profit / Loss',
            pprofitName: '% Profit',
          }),
        }));
      }
    };

    initializeComponent();
  }, [projectedFinancialPerformanceSetting, profitSetting, dispatch]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    setSelectedYears([defaultYear]);
    setLabel(defaultYear.toString());
    
    const savedTopItems = appdata.getLocalItem('pfpSelectedTopItems');
    const initialTopItems = savedTopItems || [{
      year: defaultYear,
      month: financialMonth(defaultMonth, defaultStartMonth) - 1,
    }];
    setSelectedTopItems(initialTopItems);
    appdata.setLocalItem('pfpSelectedTopItems', initialTopItems);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [defaultYear, defaultMonth, defaultStartMonth, handleResize]);

  const triggerProjectedFinancialRequests = useCallback(() => {
    const savedTopItems = appdata.getLocalItem('pfpSelectedTopItems');
    if (savedTopItems && savedTopItems.length > 0) {
      const savedDimDate = appdata.getLocalItem('pfpDimDate');
      dispatch(
        ProjectedFinancialPerformanceActions.prjfinancialperfSummaryRequest(
          [defaultYear],
          savedDimDate || [defaultDimDate],
        ),
      );
      dispatch(
        ProjectedFinancialPerformanceActions.prjfinancialperfDetailRequest(
          savedDimDate || [defaultDimDate],
          defaultDimDate,
        ),
      );
    } else {
      dispatch(
        ProjectedFinancialPerformanceActions.prjfinancialperfSummaryRequest(
          [defaultYear],
          [defaultDimDate],
        ),
      );
      dispatch(
        ProjectedFinancialPerformanceActions.prjfinancialperfDetailRequest(
          [defaultDimDate],
          defaultDimDate,
        ),
      );
    }
  }, [defaultYear, defaultDimDate, dispatch]);

  useEffect(() => {
    if (!lastFetchedCompany || !lastFetchedDimDate) {
      triggerProjectedFinancialRequests();
    }
  }, [defaultYear, defaultDimDate, lastFetchedCompany, lastFetchedDimDate, triggerProjectedFinancialRequests]);

  useEffect(() => {
    const valuesChanged =
      lastFetchedCompany !== selectedCompany ||
      lastFetchedDimDate !== defaultDimDate;

    if (
      valuesChanged &&
      lastFetchedCompany &&
      lastFetchedDimDate
    ) {
      triggerProjectedFinancialRequests();
    }
  }, [defaultDimDate, selectedCompany, defaultYear, lastFetchedCompany, lastFetchedDimDate, triggerProjectedFinancialRequests]);

  useEffect(() => {
    if (profitSetting) {
      const settings = JSON.parse(profitSetting);
      setProfitName(settings.profitName);
      setPprofitName(settings.pprofitName);
    }
  }, [tabIndex, profitSetting]);

  const handleYear = useCallback(async (event) => {
    const _defaultdimdate = makeDimDate(
      event.selectedYears[0],
      defaultMonth,
      defaultStartMonth,
    );

    await dispatch(ProjectedFinancialPerformanceActions.prjfinancialperfSummaryRequest(
      event.selectedYears,
      [_defaultdimdate],
    ));
    await dispatch(ProjectedFinancialPerformanceActions.prjfinancialperfDetailRequest(
      [_defaultdimdate],
      defaultDimDate,
    ));

    setSelectedYears(event.selectedYears);
    setLabel(event.label);
  }, [defaultMonth, defaultStartMonth, defaultDimDate, dispatch]);

  const handleFilter = useCallback(async (event) => {
    if (event.selectedYears) setSelectedYears(event.selectedYears);
    if (event.label) setLabel(event.label);
    if (event.selectedLabels) setSelectedLabels(event.selectedLabels);
    if (event.selectedTopItems) {
      setSelectedTopItems(event.selectedTopItems);
      appdata.setLocalItem('pfpSelectedTopItems', event.selectedTopItems);
    }
    if (event.selectedOption) setSelectedOption(event.selectedOption);
    if (event.showPercent !== undefined) setShowPercent(event.showPercent);
    if (event.profitName) setProfitName(event.profitName);
    if (event.pprofitName) setPprofitName(event.pprofitName);

    if (event.selectedTopItems) {
      const _dimDateKeys = event.selectedTopItems.map(d => 
        makeDimDate(d.year, d.month + 1, 1)
      ).sort((a, b) => (a > b ? -1 : 1));

      appdata.setLocalItem('pfpDimDate', _dimDateKeys);
      
      if (_dimDateKeys.length > 0) {
        await dispatch(ProjectedFinancialPerformanceActions.prjfinancialperfDetailRequest(
          _dimDateKeys,
          defaultDimDate,
        ));
      }
    }

    if (event.selectedOption || event.showPercent !== undefined) {
      await dispatch(ReportSettingActions.reportSettingUpdateRequest({
        ProjectedFinancialPerformance: JSON.stringify({
          selectedOption: event.selectedOption || selectedOption,
          showPercent: event.showPercent !== undefined ? event.showPercent : showPercent,
        }),
      }));
    }

    if (event.profitName || event.pprofitName) {
      await dispatch(ReportSettingActions.reportSettingUpdateRequest({
        ProfitTitle: JSON.stringify({
          profitName: event.profitName || profitName,
          pprofitName: event.pprofitName || pprofitName,
        }),
      }));
    }
  }, [defaultDimDate, selectedOption, showPercent, profitName, pprofitName, dispatch]);

  return (
    <div style={{
      height: '89vh',
    }} dir={dir}>
      <div className="wrapper" id="pfp_year_selector">
        {selectedYears.length > 0 ? (
          <YearSelector
            selectedYears={selectedYears}
            label={label}
            onChange={handleYear}
          />
        ) : null}
      </div>
      <div>
        <TopChart
          summaryData={summaryData}
          selectedYears={selectedYears}
          selectedLabels={selectedLabels}
          selectedTopItems={selectedTopItems}
          defaultStartMonth={defaultStartMonth}
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
          handleFilter={handleFilter}
        />
        <BottomChart
          detailData={detailData}
          selectedTopItems={selectedTopItems}
          selectedYears={selectedYears}
          tableStructure={tableStructure}
          selectedOption={selectedOption}
          showPercent={showPercent}
          profitName={profitName}
          pprofitName={pprofitName}
          handleFilter={handleFilter}
        />
      </div>

      {bubbleFlag && projectedFinancialPerformanceSummary && (
        <div
          className="reporting-tooltip"
          style={{
            position: 'fixed',
            bottom: 60,
            right: 30,
            zIndex: 1000,
            opacity: 0.5,
            transition: 'opacity 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => e.target.style.opacity = '1'}
          onMouseLeave={(e) => e.target.style.opacity = '0.5'}
        >
          <Tooltip 
            title={projectedFinancialPerformanceSummary} 
            position="top-end"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#2c3e50',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '500',
                  maxWidth: '400px',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }
              }
            }}
          >
            <InsertCommentIcon color="success" />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

ProjectedFinancialPerformance.propTypes = {
  tableStructure: PropTypes.array.isRequired,
  dir: PropTypes.string.isRequired,
  defaultYear: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  defaultDimDate: PropTypes.string.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  projectedFinancialPerformanceSetting: PropTypes.object,
  profitSetting: PropTypes.object,
  tabIndex: PropTypes.number.isRequired,
};

export default ProjectedFinancialPerformance;
